import { useState, useEffect } from "react";
import { useAgent } from "@/contexts/AgentContext";
import { InsightCard } from "./InsightCard";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { StrataLogo } from "@/components/StrataLogo";

interface ProactiveInsightsProps {
  tableId?: string;
  className?: string;
  maxInsights?: number;
}

export function ProactiveInsights({
  tableId,
  className,
  maxInsights = 3,
}: ProactiveInsightsProps) {
  const { getSuggestions, logInteraction, recordFeedback } = useAgent();
  const [insights, setInsights] = useState<Array<{
    id: string;
    type: string;
    title: string;
    description: string;
    priority: "low" | "medium" | "high";
    actionData?: Record<string, unknown>;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [interactionIds, setInteractionIds] = useState<Map<string, string>>(new Map());

  const fetchInsights = async () => {
    setLoading(true);
    const startTime = Date.now();
    
    const suggestions = await getSuggestions(tableId);
    
    // Log interaction
    const id = await logInteraction(
      "get_proactive_insights",
      { tableId },
      { suggestions },
      suggestions.length > 0 ? "success" : "partial",
      Date.now() - startTime
    );

    const newInsights = suggestions.slice(0, maxInsights).map((s, i) => ({
      id: `insight-${Date.now()}-${i}`,
      type: s.type,
      title: s.title,
      description: s.description,
      priority: s.priority,
      actionData: s.actionData,
    }));

    // Map insights to interaction for feedback
    const newInteractionIds = new Map<string, string>();
    newInsights.forEach((insight) => {
      if (id) newInteractionIds.set(insight.id, id);
    });
    setInteractionIds(newInteractionIds);

    setInsights(newInsights);
    setLoading(false);
  };

  useEffect(() => {
    fetchInsights();
  }, [tableId]);

  const handleDismiss = (insightId: string) => {
    setDismissed((prev) => new Set(prev).add(insightId));
  };

  const handleFeedback = async (insightId: string, feedback: "positive" | "negative") => {
    const interactionId = interactionIds.get(insightId);
    if (interactionId) {
      await recordFeedback(interactionId, feedback);
    }
  };

  const visibleInsights = insights.filter((i) => !dismissed.has(i.id));

  if (loading && insights.length === 0) {
    return (
      <div className={cn("flex items-center justify-center p-4", className)}>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (visibleInsights.length === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <StrataLogo size="sm" />
          AI Insights
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2"
          onClick={fetchInsights}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {visibleInsights.map((insight) => (
          <InsightCard
            key={insight.id}
            title={insight.title}
            description={insight.description}
            priority={insight.priority}
            onDismiss={() => handleDismiss(insight.id)}
            onFeedback={(feedback) => handleFeedback(insight.id, feedback)}
          />
        ))}
      </div>
    </div>
  );
}
